<template>
  <div>
    <!-- Dialog: Scheduled Time -->
    <v-dialog
      width="600"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="scheduledTimeDialogOpened"
      persistent
      @keydown.esc="scheduledTimeDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title pb-1"> Schedule message </v-card-title>
        <v-card-title class="subheading pt-0">
          {{ timeZone }}
        </v-card-title>
        <v-card-text class="mb-1 mt-1">
          <date-picker
            v-model="scheduledTime"
            type="datetime"
            :showSecond="false"
            inline
            use12h
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="editScheduledMessage(message)"
            :disabled="!scheduledTime"
            :loading="isScheduleMessageLoading"
          >
            Schedule Message
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              scheduledTimeDialogOpened = false;
              scheduledTime = null;
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-layout
      align-center
      v-if="message.unseen_user_ids.includes(currentUser.id)"
    >
      <v-divider class="mx-2" />
      <div
        class="error--text font-weight-bold"
        v-if="message.tag === 'emergency'"
      >
        EMERGENCY
      </div>
      <div
        class="warning--text font-weight-bold"
        v-else-if="message.tag === 'urgent'"
      >
        URGENT
      </div>
      <div class="primary--text font-weight-bold" v-else>New</div>
      <v-divider class="mx-2" />
    </v-layout>

    <sc-chat-bubble
      v-if="message.user_id && currentUser && !message.isHidden"
      :name="message.user_name"
      :is-reply="isReply"
      :firm="firm"
      :is-active="selectedMessageId && selectedMessageId === message.id"
      :is-me="message.user_id === currentUser.id"
      :avatar-url="message.user_avatar"
      :date="moment(message.created_at).format('lll')"
      :date-ago="moment(message.created_at).fromNow()"
    >
      <!-- Slot: Reply in Thread -->
      <div
        slot="reply"
        v-if="!getParticipants(message.threadMessages).length && !isReply"
      >
        <sc-button-group>
          <sc-button
            @click="openThread(message.id)"
            type="ghost"
            size="tiny"
            is-icon-only="true"
            icon-left="chat-reply"
            with-tooltip="true"
            >Reply in Thread</sc-button
          >
        </sc-button-group>
        <v-tooltip class="mt-1" v-if="message.tag === 'emergency'" bottom>
          <v-icon slot="activator" class="error--text" size="18"
            >warning</v-icon
          >
          <span>Emergency</span>
        </v-tooltip>
        <v-tooltip class="mt-1" v-else-if="message.tag === 'urgent'" bottom>
          <v-icon slot="activator" class="warning--text" size="18"
            >error</v-icon
          >
          <span>Urgent</span>
        </v-tooltip>
      </div>

      <!-- Partial: Attachments -->
      <sc-flex
        v-if="message && message.text && message.text.includes('$$attached$$')"
        :is-column="true"
        :gap="8"
      >
        <sc-flex :is-column="true" :gap="2">
          <sc-file
            v-for="(documentLink, i) in getDocumentLinks(message)"
            :key="i"
            :name="getDocumentName(documentLink)"
            @click="previewDocument(documentLink, message)"
          >
            <sc-button-group>
              <sc-button type="secondary" size="tiny">Preview</sc-button>
            </sc-button-group>
          </sc-file>
        </sc-flex>
        <sc-tiny>Attachment can be found under Documents/Attachments.</sc-tiny>
      </sc-flex>

      <!-- Partial: Text -->
      <sx-html class="preview ql-editor" v-else :text="message.text" />

      <!-- Partial: Scheduled -->
      <sc-flex v-if="message.scheduled" :is-column="true" :gap="8">
        <sc-small>
          <b>Scheduled:</b>
          {{
            moment(message.scheduled_time)
              .utc()
              .local()
              .format('lll')
          }}</sc-small
        >
        <sc-button-group>
          <sc-button
            icon-left="edit"
            :is-disabled="isScheduleMessageLoading"
            :is-loading="isScheduleMessageLoading"
            @click="openScheduleDialog()"
            size="tiny"
            type="secondary"
            >Edit</sc-button
          >
          <sc-button
            :is-disabled="isScheduleMessageLoading"
            @click.native.stop
            @click.prevent.stop="cancelScheduledMessage(message.id)"
            icon-left="close"
            size="tiny"
            type="secondary-danger"
            >Cancel</sc-button
          >
        </sc-button-group>
      </sc-flex>

      <!-- Slot: Generic -->
      <sc-chat-thread
        v-if="!isReply"
        @click="openThread(message.id)"
        :last-reply-date-ago="getLastReply(message)"
        :reply-count="message.threadMessages && message.threadMessages.length"
        :participant-count="getParticipants(message.threadMessages).length"
      >
        <sc-avatar
          v-for="(participant, participantKey) in getParticipants(
            message.threadMessages,
          )"
          :key="participantKey"
          :name="participant.name"
          size="24"
        ></sc-avatar>
      </sc-chat-thread>
    </sc-chat-bubble>
  </div>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import SxHtml from '@/components/sx/SxHtml';

export default {
  name: 'SxChatBubble',
  description:
    'Shared component to implement <sc-chat-bubbles> and its slotted options.',
  components: {
    SxHtml,
    DatePicker,
  },
  data() {
    return {
      scheduledTime: null,
      isScheduleMessageLoading: false,
      scheduledTimeDialogOpened: false,
    };
  },
  props: {
    isNew: {
      type: Boolean,
      default: false,
    },
    message: {
      type: Object,
      default: () => {},
      required: true,
    },
    isReply: {
      type: Boolean,
      default: false,
    },
    selectedMessageId: {
      type: Number,
      default: null,
    },
    currentUser: {
      required: true,
      type: Object,
    },
    openThread: {
      type: Function,
      default: _ => {},
    },
    firm: {
      type: String,
      default: '',
    },
    previewDocument: {
      type: Function,
      default: () => {},
    },
    refreshConversation: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    timeZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
  },
  methods: {
    moment,
    getLastReply(message) {
      let latest = new Date(message.created_at); // Use date object for comparison
      const hasThreads =
        message && message.threadMessages && message.threadMessages.length;

      if (hasThreads) {
        message.threadMessages.forEach(thread => {
          const threadCreatedAt = new Date(thread.created_at);
          if (threadCreatedAt > latest) {
            latest = threadCreatedAt;
          }
        });
      }

      return moment(latest).fromNow();
    },
    getParticipants(messages = []) {
      const uniqueNamesSet = new Set(
        messages.map(message => message.user_name),
      );
      const uniqueNamesArray = [...uniqueNamesSet].filter(Boolean);

      return uniqueNamesArray;
    },
    openScheduleDialog() {
      this.scheduledTimeDialogOpened = true;
    },
    async editScheduledMessage(message) {
      if (!message) return;

      try {
        this.isScheduleMessageLoading = true;

        const scheduledTime = message.scheduledTime
          ? message.scheduledTime
          : this.scheduledTime;
        const formattedScheduledTime = moment(scheduledTime)
          .utc()
          .local()
          .toDate();

        await API.conversation.editScheduledMessage(
          message.id,
          formattedScheduledTime,
        );

        this.message.scheduledTime = this.scheduledTime;
      } catch (error) {
        console.error(error);
      } finally {
        this.scheduledTimeDialogOpened = false;
        this.isScheduleMessageLoading = false;
        this.refreshConversation();
      }
    },
    async cancelScheduledMessage(messageId) {
      if (!messageId) return;

      try {
        this.isScheduleMessageLoading = true;

        await API.conversation.deleteScheduledMessage(messageId);
        await this.refreshConversation();
      } catch (error) {
        console.error(error);
      } finally {
        this.isScheduleMessageLoading = false;
        this.message.isHidden = true;
      }
    },
    getDocumentLinks(message) {
      return message.text.split('$$')[2].split('&!&');
    },
    // TODO move this to mixin
    getDocumentName(link) {
      return link.split('/')[link.split('/').length - 1];
    },
  },
};
</script>

<style scoped></style>
